// RunDetailPanel — a focused slide-over showing the full composed result of a
// single completed/failed agent run.
//
// The Agents Kanban and the "Agent activity" drawer both render compact run
// cards; clicking a terminal run (or a Recent-activity row) opens this panel,
// which reads the already-composed `AgentTask` from `AgentContext` and renders
// its output artefact, glass-box trace, step list, run stats and — for failed
// runs — the stored failure reason. Mirrors <ReviewItemPanel>'s shell (overlay,
// focus trap, Escape-to-close) but sits at z-[60] so it stacks above the
// z-50 activity drawer when opened from inside it.

import type { AgentTask, AgentTaskStatus } from '@septima/contract';
import {
    createContext,
    useCallback,
    useContext,
    useEffect,
    useMemo,
    useRef,
    useState,
    type ReactNode,
} from 'react';
import { Link, useLocation } from 'react-router';
import {
    AlertTriangle,
    Ban,
    CheckCircle2,
    ExternalLink,
    Loader2,
    Bot,
    Timer,
    XCircle,
    Zap,
    X,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { useFocusTrap } from '@/lib/hooks';
import { useAgent } from '@/contexts/AgentContext';
import AgentGlassBoxDetail from './AgentGlassBoxDetail';
import {
    AGENT_NAMES,
    DownloadRow,
    TASK_TYPE_LABELS,
    formatTimeOfDay,
    routeForTask,
} from './AgentTaskCard';
import { ExportToSharePointButton } from './ExportToSharePointButton';
import { agentRoleForCode } from './agent-kanban-meta';
import ArtefactResultBody from './artefact-body';

// -------- open/close context --------

interface RunDetailContextValue {
    openRun: (taskId: string) => void;
    closeRun: () => void;
}

const RunDetailContext = createContext<RunDetailContextValue | null>(null);
// Selected run id lives in its own context so cards that only *open* the panel
// do not re-render when the selection changes.
const RunDetailIdContext = createContext<string | null>(null);

export function RunDetailProvider({ children }: { children: ReactNode }) {
    const [selectedRunId, setSelectedRunId] = useState<string | null>(null);
    const openRun = useCallback((taskId: string) => setSelectedRunId(taskId), []);
    const closeRun = useCallback(() => setSelectedRunId(null), []);
    const value = useMemo<RunDetailContextValue>(
        () => ({ openRun, closeRun }),
        [openRun, closeRun],
    );
    return (
        <RunDetailContext.Provider value={value}>
            <RunDetailIdContext.Provider value={selectedRunId}>
                {children}
            </RunDetailIdContext.Provider>
        </RunDetailContext.Provider>
    );
}

export function useRunDetail(): RunDetailContextValue {
    const value = useContext(RunDetailContext);
    if (!value) throw new Error('useRunDetail must be used within a RunDetailProvider');
    return value;
}

// -------- panel shell --------

export default function RunDetailPanel() {
    const selectedRunId = useContext(RunDetailIdContext);
    const { closeRun } = useRunDetail();
    const { tasks } = useAgent();
    const location = useLocation();
    const panelRef = useRef<HTMLDivElement>(null);

    const task = useMemo(
        () => (selectedRunId ? (tasks.find((t) => t.id === selectedRunId) ?? null) : null),
        [tasks, selectedRunId],
    );
    const open = task != null;

    // Retain the last task while the drawer slides shut so the content does not
    // blank out mid-transition.
    const [displayTask, setDisplayTask] = useState<AgentTask | null>(task);
    useEffect(() => {
        if (task) setDisplayTask(task);
    }, [task]);

    useFocusTrap(panelRef, open);

    useEffect(() => {
        if (!open) return;
        const handler = (event: KeyboardEvent) => {
            if (event.key === 'Escape') {
                event.stopPropagation();
                closeRun();
            }
        };
        window.addEventListener('keydown', handler);
        return () => window.removeEventListener('keydown', handler);
    }, [open, closeRun]);

    // Close when the route changes underneath the panel (e.g. an "Open in
    // context" link), matching <AgentPanel>'s behaviour.
    const pathRef = useRef(location.pathname);
    useEffect(() => {
        if (pathRef.current === location.pathname) return;
        pathRef.current = location.pathname;
        closeRun();
    }, [location.pathname, closeRun]);

    return (
        <div
            data-testid="run-detail-panel"
            data-state={open ? 'open' : 'closed'}
            className={cn('fixed inset-0 z-[60]', !open && 'pointer-events-none')}
            aria-hidden={!open}
        >
            <div
                className={cn(
                    'absolute inset-0 bg-[var(--septima-scrim)]',
                    'transition-opacity duration-popover ease-swiss',
                    open ? 'opacity-100' : 'opacity-0',
                )}
                onClick={closeRun}
            />

            <div
                ref={panelRef}
                role="dialog"
                aria-modal="true"
                aria-labelledby="run-detail-panel-title"
                className={cn(
                    'absolute right-0 top-0 h-full w-full lg:w-[680px] xl:w-[760px]',
                    'bg-[rgb(var(--color-page))] flex flex-col',
                    'border-l border-[rgb(var(--color-divider))]',
                    'transform transition-transform duration-popover ease-swiss',
                    open ? 'translate-x-0' : 'translate-x-full',
                )}
            >
                {displayTask && (
                    <>
                        <PanelHeader task={displayTask} onClose={closeRun} />
                        <div className="flex-1 overflow-y-auto px-card-padding py-md">
                            <PanelBody task={displayTask} />
                        </div>
                    </>
                )}
            </div>
        </div>
    );
}

// -------- header --------

function PanelHeader({ task, onClose }: { task: AgentTask; onClose: () => void }) {
    const agent = AGENT_NAMES[task.type];
    const when = task.reviewedAt ?? task.completedAt ?? task.createdAt;
    return (
        <div className="sticky top-0 z-10 bg-[rgb(var(--color-page))] border-b border-[rgb(var(--color-divider))]">
            <div className="flex items-start justify-between gap-3 px-card-padding pt-md pb-sm">
                <div className="flex items-start gap-2 min-w-0">
                    <Bot className="w-4 h-4 mt-0.5 text-[rgb(var(--color-accent))] flex-shrink-0" />
                    <div className="flex flex-col gap-0.5 min-w-0">
                        <span className="text-annotation font-mono tabular-nums text-[rgb(var(--color-text-tertiary))]">
                            {[agent, agentRoleForCode(agent)].filter(Boolean).join(' · ')}
                        </span>
                        <h2
                            id="run-detail-panel-title"
                            className="text-section-title text-[rgb(var(--color-text-primary))] truncate"
                        >
                            {TASK_TYPE_LABELS[task.type]}
                        </h2>
                        <span className="text-annotation text-[rgb(var(--color-text-secondary))] truncate">
                            {[task.clientName, formatTimeOfDay(when)].filter(Boolean).join(' · ')}
                        </span>
                    </div>
                </div>
                <div className="flex items-center gap-2 flex-shrink-0">
                    <StatusPill status={task.status} />
                    <Link
                        to={routeForTask(task)}
                        onClick={onClose}
                        title="Open in context"
                        className={cn(
                            'inline-flex items-center gap-1 px-2 py-1 rounded-button',
                            'text-annotation text-[rgb(var(--color-text-secondary))]',
                            'hover:bg-[rgb(var(--color-surface-hover)/0.05)]',
                            'hover:text-[rgb(var(--color-text-primary))]',
                            'transition-colors duration-card-hover ease-swiss',
                        )}
                    >
                        <ExternalLink className="w-3.5 h-3.5" />
                        <span className="hidden sm:inline">Open</span>
                    </Link>
                    <button
                        type="button"
                        onClick={onClose}
                        aria-label="Close run detail"
                        className={cn(
                            'w-7 h-7 flex items-center justify-center rounded-button',
                            'text-[rgb(var(--color-text-secondary))]',
                            'transition-colors duration-card-hover ease-swiss',
                            'hover:bg-[rgb(var(--color-surface-hover)/0.05)]',
                            'hover:text-[rgb(var(--color-text-primary))]',
                        )}
                    >
                        <X className="w-4 h-4" strokeWidth={1.5} />
                    </button>
                </div>
            </div>
        </div>
    );
}

const STATUS_META: Record<AgentTaskStatus, { label: string; cls: string; icon: ReactNode }> = {
    queued: {
        label: 'Queued',
        cls: 'text-[rgb(var(--color-text-tertiary))] bg-[rgb(var(--color-text-tertiary)/0.12)]',
        icon: <Loader2 className="w-3 h-3" />,
    },
    running: {
        label: 'Running',
        cls: 'text-[rgb(var(--color-accent))] bg-[rgb(var(--color-accent)/0.12)]',
        icon: <Loader2 className="w-3 h-3 animate-spin" />,
    },
    review_required: {
        label: 'Needs review',
        cls: 'text-[rgb(var(--color-rag-amber))] bg-[rgb(var(--color-rag-amber)/0.12)]',
        icon: <AlertTriangle className="w-3 h-3" />,
    },
    approved: {
        label: 'Approved',
        cls: 'text-[rgb(var(--color-rag-green))] bg-[rgb(var(--color-rag-green)/0.12)]',
        icon: <CheckCircle2 className="w-3 h-3" />,
    },
    completed: {
        label: 'Completed',
        cls: 'text-[rgb(var(--color-rag-green))] bg-[rgb(var(--color-rag-green)/0.12)]',
        icon: <CheckCircle2 className="w-3 h-3" />,
    },
    rejected: {
        label: 'Rejected',
        cls: 'text-[rgb(var(--color-rag-red))] bg-[rgb(var(--color-rag-red)/0.12)]',
        icon: <XCircle className="w-3 h-3" />,
    },
    failed: {
        label: 'Failed',
        cls: 'text-[rgb(var(--color-rag-red))] bg-[rgb(var(--color-rag-red)/0.12)]',
        icon: <XCircle className="w-3 h-3" />,
    },
    unavailable: {
        label: 'Unavailable',
        cls: 'text-[rgb(var(--color-text-secondary))] bg-[rgb(var(--color-text-secondary)/0.12)]',
        icon: <Ban className="w-3 h-3" />,
    },
};

function StatusPill({ status }: { status: AgentTaskStatus }) {
    const meta = STATUS_META[status];
    return (
        <span
            className={cn(
                'inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-annotation font-medium',
                meta.cls,
            )}
        >
            {meta.icon}
            {meta.label}
        </span>
    );
}

// -------- body --------

function PanelBody({ task }: { task: AgentTask }) {
    const isFailed = task.status === 'failed' || task.status === 'unavailable';
    const artefacts = task.artefacts ?? [];

    const hasResult =
        artefacts.length > 0 ||
        task.steps.length > 0 ||
        !!task.confidence ||
        (task.citations?.length ?? 0) > 0 ||
        (task.trace?.length ?? 0) > 0 ||
        !!task.versionStamp;

    return (
        <div className="flex flex-col gap-4">
            {isFailed && <FailureBlock task={task} />}

            {artefacts.length > 0 && (
                <Section
                    title={
                        artefacts.length > 1 ? `Result · ${artefacts.length} artefacts` : 'Result'
                    }
                >
                    <div className="flex flex-col gap-3">
                        {artefacts.map((artefact, i) => (
                            <div
                                key={artefact.artefactId}
                                className={cn(
                                    i > 0 && 'pt-3 border-t border-[rgb(var(--color-divider)/0.5)]',
                                )}
                            >
                                <ArtefactResultBody artefact={artefact} />
                            </div>
                        ))}
                    </div>
                </Section>
            )}

            <OutputActions task={task} />

            {task.steps.length > 0 && (
                <Section title="Steps">
                    <ol className="flex flex-col gap-1.5 border-l border-[rgb(var(--color-divider)/0.6)] pl-3">
                        {task.steps.map((step) => (
                            <li key={step.id} className="flex items-start gap-2 -ml-[7px]">
                                <StepIcon status={step.status} />
                                <div className="flex-1 min-w-0 pt-px">
                                    <span
                                        className={cn(
                                            'text-annotation',
                                            step.status === 'error'
                                                ? 'text-[rgb(var(--color-rag-red))]'
                                                : 'text-[rgb(var(--color-text-secondary))]',
                                        )}
                                    >
                                        {step.label}
                                    </span>
                                    {step.detail && (
                                        <p className="text-annotation text-[rgb(var(--color-text-tertiary))] mt-0.5">
                                            {step.detail}
                                        </p>
                                    )}
                                </div>
                            </li>
                        ))}
                    </ol>
                </Section>
            )}

            <AgentGlassBoxDetail task={task} defaultOpen />

            <RunStats task={task} />

            {!hasResult && !isFailed && (
                <p className="text-annotation text-[rgb(var(--color-text-tertiary))]">
                    No detailed result recorded for this run.
                </p>
            )}
        </div>
    );
}

function FailureBlock({ task }: { task: AgentTask }) {
    const message =
        task.error?.message ??
        task.rejectionReason ??
        (task.status === 'unavailable'
            ? 'This agent is not available in this environment.'
            : 'This run failed without a recorded reason.');
    return (
        <div
            className={cn(
                'rounded-card border px-card-padding py-3 flex items-start gap-2.5',
                'border-[rgb(var(--color-rag-red)/0.3)] bg-[rgb(var(--color-rag-red)/0.06)]',
            )}
        >
            <XCircle
                className="w-4 h-4 mt-0.5 flex-shrink-0 text-[rgb(var(--color-rag-red))]"
                strokeWidth={1.5}
            />
            <div className="flex flex-col gap-0.5 min-w-0">
                <span className="text-card-title text-[rgb(var(--color-rag-red))]">
                    {task.status === 'unavailable' ? 'Agent unavailable' : 'Run failed'}
                </span>
                <p className="text-annotation text-[rgb(var(--color-text-secondary))] leading-snug whitespace-pre-wrap">
                    {message}
                </p>
            </div>
        </div>
    );
}

// Download / SharePoint export affordances — each self-hides when not
// applicable to the task's output, so the row only appears when there is
// something to act on.
function OutputActions({ task }: { task: AgentTask }) {
    return (
        <div className="flex items-center gap-2 flex-wrap empty:hidden">
            <DownloadRow task={task} />
            <ExportToSharePointButton task={task} />
        </div>
    );
}

function RunStats({ task }: { task: AgentTask }) {
    const hasTokens = task.totalTokens != null && task.totalTokens > 0;
    const hasLatency = task.totalLatencyMs != null && task.totalLatencyMs > 0;
    const hasModel = !!task.executedBy;
    if (!hasTokens && !hasLatency && !hasModel) return null;
    return (
        <div className="flex items-center gap-2 flex-wrap pt-1">
            {hasTokens && (
                <Chip icon={<Zap className="w-2.5 h-2.5" />}>{task.totalTokens} tok</Chip>
            )}
            {hasLatency && (
                <Chip icon={<Timer className="w-2.5 h-2.5" />}>
                    {(task.totalLatencyMs! / 1000).toFixed(1)}s
                </Chip>
            )}
            {hasModel && <Chip>{`${task.executedBy!.provider} · ${task.executedBy!.model}`}</Chip>}
            {task.conversationCount != null && task.conversationCount > 0 && (
                <Chip>
                    {task.conversationCount} turn{task.conversationCount === 1 ? '' : 's'}
                </Chip>
            )}
        </div>
    );
}

function Chip({ icon, children }: { icon?: ReactNode; children: ReactNode }) {
    return (
        <span className="inline-flex items-center gap-1 text-[10px] font-mono px-1.5 py-0.5 rounded-full bg-[rgb(var(--color-surface-secondary))] text-[rgb(var(--color-text-tertiary))]">
            {icon}
            {children}
        </span>
    );
}

function Section({ title, children }: { title: string; children: ReactNode }) {
    return (
        <section className="flex flex-col gap-2">
            <h3 className="text-card-title text-[rgb(var(--color-text-primary))]">{title}</h3>
            {children}
        </section>
    );
}

function StepIcon({ status }: { status: 'pending' | 'running' | 'done' | 'error' }) {
    if (status === 'done') {
        return (
            <CheckCircle2 className="w-3.5 h-3.5 mt-px flex-shrink-0 text-[rgb(var(--color-rag-green))]" />
        );
    }
    if (status === 'running') {
        return (
            <Loader2 className="w-3.5 h-3.5 mt-px flex-shrink-0 animate-spin text-[rgb(var(--color-accent))]" />
        );
    }
    if (status === 'error') {
        return (
            <XCircle className="w-3.5 h-3.5 mt-px flex-shrink-0 text-[rgb(var(--color-rag-red))]" />
        );
    }
    return (
        <span className="w-3.5 h-3.5 mt-px flex-shrink-0 rounded-full border border-[rgb(var(--color-divider))]" />
    );
}
